import { FC, ReactNode, useEffect, useState } from "react";
import { persistor } from "../../backend/store";

/** How long the app is willing to wait for persisted state before booting anyway. */
const REHYDRATE_TIMEOUT_MS = 3000;

/**
 * The rehydration gate, with a deadline.
 *
 * Something has to hold the first render until redux-persist reports that
 * rehydration finished, or the app paints empty lists and then jumps. But if that
 * never resolves — a wedged IndexedDB, a storage driver that hangs in some
 * locked-down browser — the app would render nothing at all and the user gets a
 * blank screen. A stale-but-present screen is recoverable; a white one is not.
 *
 * So the gate is given a deadline. Past it the app boots with whatever state it
 * has (usually the normal empty initial state, which is exactly how the app
 * behaved before persistence existed) and rehydration, if it ever lands, simply
 * applies afterwards.
 *
 * This subscribes to the persistor directly rather than wrapping redux-persist's
 * own <PersistGate>, because the deadline has to be able to fire WITHOUT changing
 * the element type above `children`. The previous version rendered <PersistGate>
 * before the deadline and a bare fragment after it: two different types in the
 * same position, so when the timer fired React tore down the entire app and
 * mounted it again. The store survived that (it lives outside React) but every
 * piece of component state did not — three seconds after each page load, any
 * screen holding its own useState was silently reset. See the test beside this
 * file. `ready` is therefore one-way: children mount once and are never
 * unmounted.
 */
export const PersistBoot: FC<{ children: ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(() => persistor.getState().bootstrapped);

  useEffect(() => {
    if (ready) return;

    // Rehydration can land between the initial render and this effect, so check
    // once here as well as on each notification.
    if (persistor.getState().bootstrapped) {
      setReady(true);
      return;
    }

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) setReady(true);
    });

    const timer = setTimeout(() => {
      console.warn(
        `[persist] Rehydration did not finish within ${REHYDRATE_TIMEOUT_MS}ms — booting without it.`
      );
      setReady(true);
    }, REHYDRATE_TIMEOUT_MS);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [ready]);

  return ready ? <>{children}</> : null;
};
